import {
  IconButton,
  Drawer,
  Box,
  Grid,
  Typography,
  Button,
  ListItemIcon,
  ListItemText,
  Dialog,
  Link as MuiLink,
  styled,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { NavigationStyles } from "./styles/Navigation.styles";

// components
import AppMenu from "./AppMenu";
import { Toolbar } from "@mui/material";
import { Link, NavLink, useHistory } from "react-router-dom";
import RouteItem from "src/model/RouteItem.model";
import CalebAndBrownLogo from "src/icons/cnbDesktop";
import { Logout, Settings } from "@mui/icons-material";
import { useIsMobile } from "src/utils/hooks/useIsMobile";
import { trackEvent } from "src/Amplitude";
import { useContext } from "react";
import AuthContext from "src/providers/AuthProvider";
import usePermission from "src/utils/hooks/usePermission";
import { useTSAccountProtection } from "@transmitsecurity/riskid-reactjs-ts";

// define interface to represent component props
interface NavigationProps {
  routes: RouteItem[];
  open: boolean;
  handleMenuClose: () => void;
}

const Navigation = ({ routes, open, handleMenuClose }: NavigationProps) => {
  // REDUX
  const isMobile = useIsMobile();
  const { isGuest } = usePermission();
  const { logout } = useContext(AuthContext);
  const { clearUser } = useTSAccountProtection();

  // STYLES
  const classes = NavigationStyles();

  // REACT ROUTER
  const history = useHistory();

  // FUNCTIONS
  async function handleClick() {
    // close drawer and logout when use clicks on #logout
    trackEvent("Select", { event: "Logout" }, "Navigation");
    // clears the user context for all subsequent events in the browser session
    await clearUser();
    handleMenuClose();
    await logout();
  }

  function navigate(path: string) {
    handleMenuClose();
    history.push(path);
  }
  return (
    <>
      {/* MOBILE */}
      {isMobile && (
        <Dialog
          fullScreen
          open={open}
          onClose={handleMenuClose}
          // TransitionComponent={Transition}
          className={classes.mobileDialog}>
          <Grid container className={classes.toolbar}>
            <Grid item xs={2}>
              <IconButton onClick={handleMenuClose}>
                <CloseIcon fontSize="large" htmlColor="#ffffff" />
              </IconButton>
            </Grid>
            <Grid item xs={8} style={{ paddingBottom: "6px" }}>
              <CalebAndBrownLogo height={36} />
            </Grid>
          </Grid>
          <Grid container className={classes.DrawerMenuComponent}>
            <Grid item xs={12}>
              <AppMenu routes={routes} handleMenuClose={handleMenuClose} />
            </Grid>
          </Grid>

          {/* MOBILE FOOTER */}
          <Box className={classes.Footer}>
            <Grid container className={classes.mobileMenuStyles}>
              <Grid item xs={12} className={classes.menuItem}>
                <Button
                  disableRipple
                  onClick={() => {
                    if (isGuest) trackEvent("Guest_Is_Redirected_To_Verification");
                    navigate(isGuest ? "/application" : "/settings/profile");
                  }}
                  startIcon={<Settings className={classes.icon} />}>
                  <Typography className={classes.fontStyle}>Settings</Typography>
                </Button>
              </Grid>
              <Grid item xs={12} className={classes.menuItem}>
                <Button disableRipple startIcon={<Logout className={classes.icon} />}>
                  <Typography className={classes.fontStyle} onClick={handleClick}>
                    Logout
                  </Typography>
                </Button>
              </Grid>
            </Grid>
          </Box>
        </Dialog>
      )}

      {/* DESKTOP */}
      <Drawer className={classes.drawer} variant="permanent">
        <NavLink to="/portfolio">
          <Toolbar>
            <CalebAndBrownLogo height="65px" />
          </Toolbar>
        </NavLink>
        <Box display="flex" flexDirection="column" height="100%">
          <AppMenu routes={routes} handleMenuClose={handleMenuClose} />
          <Grid container className={classes.FooterDesktop}>
            <Grid item xs={12} className={classes.menuItem}>
              <Link to={isGuest ? "/application" : "/settings/profile"} className={classes.link}>
                <Box sx={{ display: "flex", alignItems: "center" }}>
                  <ListItemIcon sx={{ minWidth: "auto", paddingRight: "0.5rem" }}>
                    <Settings className={classes.icon} />
                  </ListItemIcon>
                  <ListItemText
                    onClick={() => trackEvent("Select_Navigation", { event: "Settings" })}
                    primary={<Typography className={classes.fontStyle}>Settings</Typography>}
                  />
                </Box>
              </Link>
            </Grid>
            <Grid item xs={12} className={classes.menuItem}>
              <Button disableRipple startIcon={<Logout className={classes.icon} />}>
                <Typography className={classes.fontStyle} onClick={handleClick}>
                  Logout
                </Typography>
              </Button>
            </Grid>
          </Grid>
        </Box>
      </Drawer>
    </>
  );
};

export default Navigation;
